import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Container,
    Divider,
    Grid,
    Stack,
    Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import {
    FaQrcode,
    FaLink,
    FaFileAlt,
    FaAddressCard,
    FaDownload,
    FaChartBar,
    FaPen,
    FaPoll,
    FaTag,
    FaLayerGroup,
    FaLock,
    FaBullseye,
} from "react-icons/fa";
import { SectionHeading } from "./utils";

/* ---------- Styled bits ---------- */

const ServiceCard = styled(Card)(({ theme }) => ({
    height: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: 22,
    transition: "transform 260ms ease, box-shadow 260ms ease, border-color 260ms ease",
    backdropFilter: "blur(10px)",
    border: `1px solid ${theme.palette.mode === "light" ? "rgba(99,102,241,0.2)" : theme.palette.divider}`,
    background:
        theme.palette.mode === "light"
            ? "rgba(255,255,255,0.78)"
            : theme.palette.background.default,
    "&:hover": {
        transform: "translateY(-8px) scale(1.01)",
        boxShadow: "0 20px 46px rgba(67,56,202,0.2)",
        borderColor:
            theme.palette.mode === "light"
                ? theme.palette.grey[300]
                : theme.palette.grey[700],
    },
}));

const FeaturedCard = styled(ServiceCard)(({ theme }) => ({
    position: "relative",
    borderColor:
        theme.palette.mode === "light"
            ? theme.palette.primary.light
            : theme.palette.primary.dark,
    boxShadow: theme.shadows[8],
    background:
        theme.palette.mode === "light"
            ? `linear-gradient(180deg, rgba(255,255,255,0.78) 0%, ${theme.palette.action.hover} 100%)`
            : `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.action.selected} 100%)`,
    "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        borderRadius: 22,
        padding: 1,
        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.success.main})`,
        WebkitMask:
            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
        WebkitMaskComposite: "xor",
        maskComposite: "exclude",
        pointerEvents: "none",
    },
}));

const IconWrap = styled("div")(({ theme }) => ({
    display: "grid",
    placeItems: "center",
    width: 64,
    height: 64,
    borderRadius: 22,
    marginInline: "auto",
    color:
        theme.palette.mode === "light"
            ? theme.palette.primary.main
            : theme.palette.primary.light,
    background:
        theme.palette.mode === "light"
            ? theme.palette.primary.light + "22"
            : theme.palette.primary.dark + "33",
}));

/* ---------- Data ---------- */

const services = [
    {
        key: "dynamic",
        title: "Dynamic QR Codes",
        description:
            "Editable QR codes you can modify anytime—no need to reprint when content changes.",
        icon: <FaQrcode size={28} />,
        tags: ["Editable", "A/B test", "Routing"],
        featured: true, // will render the gradient frame + badge
    },
    {
        key: "restaurant",
        title: "Restaurant Menu management",
        description: "Convert your restaurant menu into scannable QR codes instantly.",
        icon: <FaLink size={28} />,
        tags: ["Fast", "Shareable", "Accessible"],
    },
    {
        key: "product",
        title: "Product Authentication",
        description: "Protect customer trust with QR-powered verification journeys for packaged products.",
        icon: <FaLink size={28} />,
        tags: ["Trust", "Verification", "Traceable"],
    },
    {
        key: "website",
        title: "Website QR Codes",
        description: "Convert your website URLs into scannable QR codes instantly.",
        icon: <FaLink size={28} />,
        tags: ["Fast", "Shareable", "UTM-ready"],
    },
    {
        key: "surveys",
        title: "Surveys QR Codes",
        description: "Collect customer feedback and responses instantly using dynamic survey QR pages.",
        icon: <FaPoll size={28} />,
        tags: ["Feedback", "Responses", "Insights"],
    },
    {
        key: "shorteners",
        title: "Link Shorteners",
        description: "Create clean short links behind QR codes and update destinations whenever you need.",
        icon: <FaLink size={28} />,
        tags: ["Short URLs", "Editable", "Trackable"],
    },
    {
        key: "discount",
        title: "Discount Codes",
        description: "Deliver QR-powered discount and promo codes to boost conversions and repeat purchases.",
        icon: <FaTag size={28} />,
        tags: ["Promos", "Coupons", "Sales"],
    },
    {
        key: "smart-landing",
        title: "Smart Page QR",
        description: "Use one QR to launch a mini landing page with Website, WhatsApp, Call, Pay, and social links.",
        icon: <FaLayerGroup size={28} />,
        tags: ["Multi-link", "Mini Site", "Actions"],
    },
    {
        key: "private-qr",
        title: "Secure / Private QR Codes",
        description: "Protect premium content with password locks, expiry, scan limits, and geo-restricted access.",
        icon: <FaLock size={28} />,
        tags: ["Password", "Expiry", "Geo-fence"],
    },
    {
        key: "offline-campaign",
        title: "Offline Campaign Tracker",
        description: "Tag offline campaigns (like Billboard A vs B) and compare scan performance from each channel.",
        icon: <FaBullseye size={28} />,
        tags: ["Campaign Tags", "Compare", "Attribution"],
    },
    {
        key: "vcard",
        title: "vCard QR Codes",
        description: "Digitize your contacts with QR vCards for quick saves.",
        icon: <FaAddressCard size={28} />,
        tags: ["Contact", "vCard", "One-tap save"],
    },
    {
        key: "file",
        title: "File QR Codes",
        description:
            "Generate QR codes for documents and files: PDFs, images, slides, and more.",
        icon: <FaFileAlt size={28} />,
        tags: ["PDF", "Images", "Slides"],
    },
    {
        key: "analytics",
        title: "Scan Analytics",
        description:
            "Track and analyze QR scans by time, device, and location with clean reports.",
        icon: <FaChartBar size={28} />,
        tags: ["Reports", "CSV", "Attribution"],
        ctaLabel: "View Scan Analytics",
    },
    {
        key: "download",
        title: "Download Options",
        description:
            "Export QR codes in PNG, JPEG, SVG, and PDF for crisp print and digital use.",
        icon: <FaDownload size={28} />,
        tags: ["PNG", "SVG", "PDF"],
        ctaLabel: "Download Options",
    },
    {
        key: "customize",
        title: "Customise Your QR",
        description:
            "Fine-tune colors, eyes, patterns, and branding frames—make it unmistakably yours.",
        icon: <FaPen size={28} />,
        tags: ["Branding", "Frames", "Logos"],
        ctaLabel: "Customise Your QR",
    },
];

/* ---------- Component ---------- */

const SIGN_UP_ROUTE = "/auth?mode=signup";

const ServicesSection = () => {
    return (
        <Box component="section">
            <Container sx={{ py: { xs: 5, sm: 8 } }}>
                <Stack spacing={1} sx={{ textAlign: "center", mb: { xs: 2, sm: 3 } }}>
                    <SectionHeading>Our Services</SectionHeading>
                    <Typography variant="body1" color="text.secondary">
                        Everything you need to create, brand, deploy, and measure QR codes—plus
                        flexible downloads and analytics that make sense.
                    </Typography>
                </Stack>

                <Grid
                    container
                    rowSpacing={{ xs: 2, sm: 3, md: 4 }}
                    columnSpacing={{ xs: 2, sm: 3, md: 4 }}
                    alignItems="stretch"
                    sx={{ mt: { xs: 1, sm: 2, md: 3 } }}
                >
                    {services.map((s) => {
                        const CardComp = s.featured ? FeaturedCard : ServiceCard;
                        const primaryActionTarget = s.cta?.to || SIGN_UP_ROUTE;
                        const ctaLabel = s.ctaLabel || `Create ${s.title}`;
                        return (
                            <Grid key={s.key} item xs={12} sm={6} md={4}>
                                <CardComp elevation={s.featured ? 0 : 2}>
                                    {/* Badge for featured */}
                                    {s.featured && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                top: 12,
                                                left: { xs: 8, sm: 12 },
                                                right: { xs: 8, sm: "auto" },
                                                zIndex: 1,
                                            }}
                                        >
                                            <Chip
                                                size="small"
                                                color="primary"
                                                label="Most Popular" sx={{ fontWeight: 700, bgcolor: "#6366f1", maxWidth: { xs: "calc(100vw - 72px)", sm: "none" } }}
                                            />
                                        </Box>
                                    )}

                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Stack spacing={2} alignItems="center" textAlign="center">
                                            <IconWrap aria-hidden="true">{s.icon}</IconWrap>

                                            <Typography
                                                component="h3"
                                                sx={{
                                                    fontWeight: 800,
                                                    fontSize: { xs: "1.1rem", sm: "1.35rem" },
                                                    letterSpacing: 0.2,
                                                }}
                                            >
                                                {s.title}
                                            </Typography>

                                            <Typography color="text.secondary">{s.description}</Typography>

                                            {/* tags */}
                                            {s.tags && (
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    useFlexGap
                                                    flexWrap="wrap"
                                                    justifyContent="center"
                                                >
                                                    {s.tags.map((t) => (
                                                        <Chip
                                                            key={t}
                                                            size="small"
                                                            variant="outlined"
                                                            label={t}
                                                            sx={{
                                                                borderRadius: 999,
                                                                fontWeight: 600,
                                                                borderStyle: "dashed",
                                                            }}
                                                        />
                                                    ))}
                                                </Stack>
                                            )}
                                        </Stack>
                                    </CardContent>

                                    <Divider />
                                    <CardActions
                                        sx={{
                                            p: 2,
                                            pt: 1.5,
                                            display: "grid",
                                            gridTemplateColumns: "1fr",
                                            gap: 1,
                                        }}
                                    >
                                        <Button
                                            fullWidth
                                            variant={s.featured ? "contained" : "outlined"}
                                            color="primary"
                                            component={RouterLink}
                                            to={primaryActionTarget}
                                            sx={{
                                                fontWeight: 800,
                                                borderRadius: 999,
                                                py: { xs: 1, sm: 0.8 },
                                                textAlign: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            {ctaLabel}
                                        </Button>
                                    </CardActions>
                                </CardComp>
                            </Grid>
                        );
                    })}
                </Grid>
            </Container>
        </Box>
    );
};

export default ServicesSection;
